import { useState, useCallback } from "react";
import { GenericService } from "../services/genericService";

// ================================================================
//  useGeneric — reusable hook for any table
//
//  Usage example:
//
//  const agencyHook = useGeneric<AgencyDTO>({
//    tableName:     "agencies",
//    idColumn:      "agency_id",
//    orderBy:       "agency_name",
//    removeOnInsert: ["agency_id"],
//    filters:       { mobile_number: "9539944599" },
//  });
// ================================================================

interface UseGenericOptions {
  tableName:      string;
  idColumn:       string;
  orderBy?:       string;
  removeOnInsert?: string[];
  filters?:       Record<string, any>;
}

export function useGeneric<T>({
  tableName,
  idColumn,
  orderBy,
  removeOnInsert = [],
  filters,
}: UseGenericOptions) {
  const [data, setData]       = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  // ── Fetch all ──────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await GenericService.getAll<T>(tableName, orderBy, filters);
      setData(result);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tableName, orderBy, JSON.stringify(filters)]);

  // ── Get by ID ──────────────────────────────────────────────
  const getById = useCallback(
    async (id: number | string): Promise<T | null> => {
      try {
        return await GenericService.getById<T>(tableName, idColumn, id);
      } catch (err: any) {
        setError(err.message);
        return null;
      }
    },
    [tableName, idColumn]
  );

  // ── Save (insert or update) ────────────────────────────────
  const save = useCallback(
    async (row: any): Promise<void> => {
      try {
        if (row[idColumn]) {
          await GenericService.update(tableName, idColumn, row);
        } else {
          await GenericService.insert(tableName, row, removeOnInsert);
        }
        await fetchAll(); // refresh list
      } catch (err: any) {
        setError(err.message);
        throw err;        // re-throw so the form can catch it
      }
    },
    [tableName, idColumn, removeOnInsert, fetchAll]
  );

  // ── Delete ─────────────────────────────────────────────────
  const remove = useCallback(
    async (id: number | string): Promise<void> => {
      try {
        await GenericService.delete(tableName, idColumn, id);
        await fetchAll();
      } catch (err: any) {
        setError(err.message);
        throw err;
      }
    },
    [tableName, idColumn, fetchAll]
  );

  return { data, loading, error, fetchAll, getById, save, remove };
}